import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { useSiteEntities } from '../../hooks/useSiteEntities';
import SiteSelector from '../SiteSelector';
import D3HybridViewer from '../graph-viz/D3HybridViewer';
import DetailsPanel from '../graph-viz/DetailsPanel';
import {
  processData,
  getSingleBranchExpansion,
  getPathExpansionFromNode,
  getExpansionToSeeType,
  getVisibleNodes,
  calculateExpansionForType
} from '../graph-viz/utils/graphUtils';
import { PVSiteData, Node, Edge, GraphNode } from '../graph-viz/types';
import { Layers } from 'lucide-react';

const NetworkViewerTab: React.FC = () => {
  // Use Redux instead of AppContext
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const { entities, connections, isLoadingEntities, isLoadingConnections } = useSiteEntities();
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);

  // Local State for Graph Viewer
  const [orientation, setOrientation] = useState<'TD' | 'LR'>('LR');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [powerVisMode, setPowerVisMode] = useState<'none' | 'shadow' | 'halo' | 'bar'>('none');
  const [highlightedPath, setHighlightedPath] = useState<Set<string>>(new Set());
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Deferred processing state - allows spinner to render before heavy computation
  const [graphData, setGraphData] = useState<{ nodes: GraphNode[], links: any[] }>({ nodes: [], links: [] });
  const [isProcessing, setIsProcessing] = useState(false);
  const processedDataKeyRef = useRef<string>('');
  const initializedForRef = useRef<string>('');

  // Create a stable key for the current raw data to detect changes
  const rawDataKey = useMemo(() => {
    if (!entities.length) return '';
    // Use first entity ID + count as a simple fingerprint
    const firstEntity = entities[0];
    const firstId = firstEntity?.entity_id || firstEntity?.id || '';
    return `${firstId}-${entities.length}-${connections.length}`;
  }, [entities, connections]);

  // Deferred data processing - runs after spinner renders
  useEffect(() => {
    // Skip if no data or already processed this data
    if (!rawDataKey || rawDataKey === processedDataKeyRef.current) {
      return;
    }

    // Mark as processing to show spinner
    setIsProcessing(true);

    // Defer heavy computation to let spinner render
    const frameId = requestAnimationFrame(() => {
      // Map Entities to Node format
      const nodes: Node[] = entities.map(entity => {
        const entityId = entity.entity_id || entity.id || (entity as any).uuid;
        const entityName = entity.entity_name || entity.name || (entity as any).primary_id;
        const componentType = entity.pvcollada_component_type || entity.entity_type_name || entity.concept_category || (entity as any).component_type || 'Unknown';
        const metadata = entity.entity_metadata || entity.metadata || entity.properties || {};

        // pref can be at top level of entity (from API) or in metadata
        // Format is { p: pageNumber } or { page: pageNumber }
        const rawPref = entity.pref || metadata.pref || metadata.page_ref;
        let pref: any = undefined;
        if (rawPref) {
          if (typeof rawPref === 'object' && rawPref.p != null) {
            // API format: { p: 17 }
            pref = rawPref.p;
          } else if (typeof rawPref === 'object' && rawPref.page != null) {
            pref = rawPref.page;
          } else {
            pref = rawPref;
          }
        }

        return {
          component_type: componentType.toLowerCase().replace(/ /g, '_'),
          "Primary ID": entityName,
          Aliases: [entityId],
          component_order: entity.component_order || metadata.component_order || null,
          properties: metadata,
          configuration: entity.configuration || {},
          pref: pref
        };
      });

      // Map Connections to Edge format
      const edges: Edge[] = connections.map(conn => {
        const sourceEntity = entities.find(e => (e.entity_id || e.id) === conn.source_entity_id);
        const targetEntity = entities.find(e => (e.entity_id || e.id) === conn.target_entity_id);

        const sourceId = sourceEntity ? (sourceEntity.entity_name || sourceEntity.name) : conn.source_entity_id;
        const targetId = targetEntity ? (targetEntity.entity_name || targetEntity.name) : conn.target_entity_id;

        let signal = conn.properties?.signal || 'DC';
        const typeLower = conn.connection_type.toLowerCase();

        // Only override if signal is not explicitly set or is generic
        if (!conn.properties?.signal) {
          if (typeLower.includes('ac')) signal = 'AC';
          else if (typeLower.includes('data') || typeLower.includes('modbus') || typeLower.includes('comm')) signal = 'COMM';
        }

        return {
          source: sourceId,
          target: targetId,
          relation: conn.connection_type,
          signal: signal,
          properties: conn.properties || {},
          configuration: conn.configuration || {}
        };
      });

      const pvSiteData: PVSiteData = {
        nodes,
        edges,
        project_metdata: {
          project_name: "Solar Farm",
          project_address: null,
          project_lat_lon: null,
          project_capacity_mw: null
        }
      };

      const processed = processData(pvSiteData);
      processedDataKeyRef.current = rawDataKey;
      setGraphData(processed);
      setIsProcessing(false);
    });

    return () => cancelAnimationFrame(frameId);
  }, [rawDataKey, entities, connections]);

  // Create a stable identifier for the current site's data
  const siteDataKey = useMemo(() => {
    if (graphData.nodes.length === 0) return '';
    const rootNode = graphData.nodes.find(n => n.tier === 0);
    return rootNode?.id || '';
  }, [graphData.nodes]);

  // Initialize expansion state with single branch when NEW site data loads
  // Only runs once per site (not during pagination or partial loads)
  useEffect(() => {
    // Skip if still loading, no data, or already initialized for this site
    if (isLoadingEntities || isLoadingConnections || !siteDataKey || siteDataKey === initializedForRef.current) {
      return;
    }

    // Initialize for new site
    const initial = getSingleBranchExpansion(graphData.nodes, graphData.links);
    setExpandedIds(initial);
    setHighlightedPath(new Set());
    initializedForRef.current = siteDataKey;
  }, [isLoadingEntities, isLoadingConnections, siteDataKey, graphData.nodes, graphData.links]);

  // Calculate visible nodes based on expansion state
  const visibleNodeIds = useMemo(() => {
    return getVisibleNodes(graphData.nodes, graphData.links, expandedIds);
  }, [graphData.nodes, graphData.links, expandedIds, siteDataKey, isLoadingEntities, isLoadingConnections]);

  // Use local selectedComponents state
  const selectedNodeIds = new Set(selectedComponents);

  const handleNodeClick = useCallback((node: GraphNode) => {
    // Single select and expand path (matching reference implementation)
    setSelectedComponents([node.id]);

    // Expand a full path from grid to module, centered on the clicked node
    const pathExpansion = getPathExpansionFromNode(node.id, graphData.nodes, graphData.links);
    setExpandedIds(pathExpansion);
    setHighlightedPath(pathExpansion);

    // If the node has a pref (page reference), navigate the as-built viewer to that page
    const pref = node.details?.pref;
    if (pref != null) {
      // pref is normalized to a page number in the entity mapping
      const page = typeof pref === 'number' ? pref : parseInt(String(pref), 10);

      if (!isNaN(page)) {
        window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
          detail: {
            nodeId: node.id,
            nodeLabel: node.label || node.id,
            page: page,
            pageRefs: [{ label: node.label || node.id, page: page }]
          }
        }));
      }
    }
  }, [setSelectedComponents, graphData.nodes, graphData.links]);

  const handleToggleExpand = useCallback((nodeId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setSelectedComponents([]);
    setHighlightedPath(new Set());
  }, [setSelectedComponents]);

  // Handle multi-node selection (from power distribution table, etc.)
  const handleMultiNodeSelect = useCallback((nodeIds: string[]) => {
    // Select all the nodes
    setSelectedComponents(nodeIds);
    setHighlightedPath(new Set()); // Clear path highlighting for multi-select

    // Expand ancestors to make them visible (matching reference)
    const newExpanded = new Set(expandedIds);
    nodeIds.forEach(nodeId => {
      const node = graphData.nodes.find(n => n.id === nodeId);
      if (node) {
        // Walk up and expand all ancestors
        let curr: GraphNode | undefined = node.parentId ? graphData.nodes.find(n => n.id === node.parentId) : undefined;
        while (curr) {
          newExpanded.add(curr.id);
          const parentId = curr.parentId;
          curr = parentId ? graphData.nodes.find(n => n.id === parentId) : undefined;
        }
      }
    });
    setExpandedIds(newExpanded);
  }, [setSelectedComponents, expandedIds, graphData.nodes]);

  // Handle legend click to filter/expand to see a specific type
  const handleLegendClick = useCallback((type: string) => {
    const expansion = getExpansionToSeeType(graphData.nodes, graphData.links, type);
    setExpandedIds(expansion);
    setSelectedComponents([]);
    setHighlightedPath(new Set());
  }, [graphData.nodes, graphData.links, setSelectedComponents]);

  // Handle toggling visibility of a type in the hierarchy
  const handleToggleType = useCallback((sourceNodeIds: string[], type: string) => {
    const newExpanded = calculateExpansionForType(
      graphData.nodes,
      graphData.links,
      expandedIds,
      sourceNodeIds,
      type
    );
    setExpandedIds(newExpanded);
  }, [graphData.nodes, graphData.links, expandedIds]);

  // Expand all/none
  const handleExpandAll = () => {
    if (expandedIds.size > 0) {
      setExpandedIds(new Set());
    } else {
      const allIds = new Set(graphData.nodes.map(n => n.id));
      setExpandedIds(allIds);
    }
  };

  // Show loading spinner while fetching data or processing
  if (isLoadingEntities || isLoadingConnections || isProcessing) {
    const message = isProcessing ? 'Processing Graph...' : 'Loading Graph Data...';
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#0d1830] text-white">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <div>{message}</div>
        </div>
      </div>
    );
  }

  // Show "No Site Selected" only if no site is selected in Redux
  if (!currentSite) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-[#0d1830] text-gray-400 p-6">
        <div className="flex flex-col items-center mb-6">
          <Layers size={48} className="mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">No Site Selected</div>
          <p className="mt-2 text-sm opacity-75 mb-4">Please select a site to view the network.</p>
        </div>
        <div className="w-full max-w-md">
          <SiteSelector />
        </div>
      </div>
    );
  }

  // Show empty state if site is selected but no entities loaded yet (and not loading)
  if (graphData.nodes.length === 0 && !isLoadingEntities && !isProcessing) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-[#0d1830] text-gray-400 p-6">
        <div className="flex flex-col items-center mb-6">
          <Layers size={48} className="mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">No Components Found</div>
          <p className="mt-2 text-sm opacity-75 mb-4">This site has no components to display.</p>
        </div>
        <div className="w-full max-w-md">
          <SiteSelector />
        </div>
      </div>
    );
  }

  const selectedNodes = graphData.nodes.filter(n => selectedNodeIds.has(n.id));



  return (
    <div className="w-full h-full bg-[#0d1830] relative overflow-hidden flex flex-col">
      {/* Site Selector - Top Bar */}
      <div className="p-4 border-b border-engramiq-green/20 bg-surface-800/30">
        <div className="max-w-md">
          <SiteSelector />
        </div>
      </div>

      {/* Graph Viewer - Full Size */}
      <div className="flex-1 relative">
        <D3HybridViewer
            nodes={graphData.nodes}
            links={graphData.links}
            onNodeClick={handleNodeClick}
            orientation={orientation}
            expandedIds={expandedIds}
            onToggleExpand={handleToggleExpand}
            powerVisMode={powerVisMode}
            selectedNodeIds={selectedNodeIds}
            onBackgroundClick={handleBackgroundClick}
            visibleNodeIds={visibleNodeIds}
            highlightedPath={highlightedPath}
          />
      </div>

      {/* Details Panel (Sidebar) - Absolute overlay */}
      <div className="absolute top-0 right-0 h-full z-10">
        <DetailsPanel
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          selectedNodes={selectedNodes}
          onClose={() => {
            setSelectedComponents([]);
            setHighlightedPath(new Set());
          }}
          nodes={graphData.nodes}
          links={graphData.links}
          onToggleType={handleToggleType}
          expandedIds={expandedIds}
          powerVisMode={powerVisMode}
          onPowerSelect={(filter) => {
            if (filter) {
              const matching = graphData.nodes.filter(n =>
                n.tier === filter.tier &&
                Math.abs((n.cumulativePower || 0) - filter.power) < 0.1
              );
              setSelectedComponents(matching.map(n => n.id));
              setHighlightedPath(new Set());
            }
          }}
          onPropertySelect={(key, value) => {
            const matching = graphData.nodes.filter(n =>
              (n.details.properties && n.details.properties[key] === value) ||
              (n.details.configuration && n.details.configuration[key] === value)
            );
            setSelectedComponents(matching.map(n => n.id));
            setHighlightedPath(new Set());
          }}
          onLegendClick={handleLegendClick}
          visibleNodeIds={visibleNodeIds}
          onMultiNodeSelect={handleMultiNodeSelect}
          // New Props for Sidebar Controls
          orientation={orientation}
          onOrientationChange={setOrientation}
          onPowerVisChange={setPowerVisMode}
          onExpandAll={handleExpandAll}
        />
      </div>
    </div>
  );
};

export default NetworkViewerTab;
